import { apiService } from "./api"
import type {
  Restaurant,
  Table,
  Menu,
  MenuSection,
  Product,
  ProductVariant,
  ProductAddon,
  Order,
  OrderItem,
  CreateTableRequest,
  UpdateTableRequest,
  CreateMenuRequest,
  CreateSectionRequest,
  CreateProductRequest,
  CreateVariantRequest,
  CreateAddonRequest,
  UpdateImojeConfigRequest,
} from "@/types"

export const restaurantService = {
  // Restaurant management
  async getRestaurants(): Promise<Restaurant[]> {
    return apiService.get<Restaurant[]>("/restaurants")
  },

  async getAllRestaurants(): Promise<Restaurant[]> {
    return apiService.get<Restaurant[]>("/restaurants")
  },

  // Admin methods
  async getAllRestaurantsAdmin(): Promise<Restaurant[]> {
    return apiService.get<Restaurant[]>("/admin/restaurants")
  },

  async createRestaurant(data: any): Promise<Restaurant> {
    return apiService.post<Restaurant>("/admin/restaurants", data)
  },

  async getUsers(): Promise<any[]> {
    return apiService.get<any[]>("/admin/users")
  },

  async getUsersPaginated(
    page: number,
    limit: number,
    role?: string,
    search?: string
  ): Promise<{
    data: any[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (role) params.append("role", role)
    if (search) params.append("search", search)

    return apiService.get<{
      data: any[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>(`/admin/users/paginated?${params.toString()}`)
  },

  async createUser(data: any): Promise<any> {
    return apiService.post<any>("/admin/users/register", data)
  },

  async getRestaurant(restaurantId: string): Promise<Restaurant> {
    return apiService.get<Restaurant>(`/restaurants/${restaurantId}`)
  },

  async updateRestaurant(restaurantId: string, data: Partial<Restaurant>): Promise<Restaurant> {
    return apiService.put<Restaurant>(`/restaurants/${restaurantId}`, data)
  },

  async deleteRestaurantLogo(restaurantId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/logo`)
  },

  async deleteRestaurantCoverImage(restaurantId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/cover-image`)
  },

  async deleteProductImage(restaurantId: string, productId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/products/${productId}/image`)
  },

  // Table management
  async getTables(restaurantId: string): Promise<Table[]> {
    return apiService.get<Table[]>(`/restaurants/${restaurantId}/tables`)
  },

  async getTableQrStats(
    restaurantId: string,
    tableId: string
  ): Promise<{ totalScans: number; last30DaysScans: number }> {
    return apiService.get<{ totalScans: number; last30DaysScans: number }>(
      `/restaurants/${restaurantId}/tables/${tableId}/qr-stats`
    )
  },

  async createTable(restaurantId: string, data: CreateTableRequest): Promise<Table> {
    return apiService.post<Table>(`/restaurants/${restaurantId}/tables`, data)
  },

  async updateTable(
    restaurantId: string,
    tableId: string,
    data: UpdateTableRequest
  ): Promise<Table> {
    return apiService.put<Table>(`/restaurants/${restaurantId}/tables/${tableId}`, data)
  },

  async deleteTable(restaurantId: string, tableId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/tables/${tableId}`)
  },

  // Menu management
  async getMenus(restaurantId: string): Promise<Menu[]> {
    return apiService.get<Menu[]>(`/restaurants/${restaurantId}/menu/all`)
  },

  async getMenu(restaurantId: string, menuId: string): Promise<Menu> {
    return apiService.get<Menu>(`/restaurants/${restaurantId}/menu/${menuId}/basic`)
  },

  async createMenu(restaurantId: string, data: CreateMenuRequest): Promise<Menu> {
    return apiService.post<Menu>(`/restaurants/${restaurantId}/menu`, data)
  },

  async updateMenu(
    restaurantId: string,
    menuId: string,
    data: Partial<CreateMenuRequest>
  ): Promise<Menu> {
    return apiService.put<Menu>(`/restaurants/${restaurantId}/menu/${menuId}`, data)
  },

  async deleteMenu(restaurantId: string, menuId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/menu/${menuId}`)
  },

  // Section management
  async getMenuSections(restaurantId: string, menuId: string): Promise<MenuSection[]> {
    return apiService.get<MenuSection[]>(`/restaurants/${restaurantId}/menu/${menuId}/sections`)
  },
  async createSection(
    restaurantId: string,
    menuId: string,
    data: CreateSectionRequest
  ): Promise<MenuSection> {
    return apiService.post<MenuSection>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections`,
      data
    )
  },

  async updateSection(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    data: Partial<CreateSectionRequest>
  ): Promise<MenuSection> {
    return apiService.put<MenuSection>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}`,
      data
    )
  },

  async deleteSection(restaurantId: string, menuId: string, sectionId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}`)
  },

  // Product management
  async getSectionProducts(
    restaurantId: string,
    menuId: string,
    sectionId: string
  ): Promise<Product[]> {
    return apiService.get<Product[]>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products`
    )
  },

  async createProduct(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    data: CreateProductRequest
  ): Promise<Product> {
    return apiService.post<Product>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products`,
      data
    )
  },

  async updateProduct(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    data: Partial<CreateProductRequest>
  ): Promise<Product> {
    return apiService.put<Product>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}`,
      data
    )
  },

  async deleteProduct(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}`
    )
  },

  // Variant management
  async createVariant(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    data: CreateVariantRequest
  ): Promise<ProductVariant> {
    return apiService.post<ProductVariant>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/variants`,
      data
    )
  },

  async updateVariant(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    variantId: string,
    data: Partial<CreateVariantRequest>
  ): Promise<ProductVariant> {
    return apiService.put<ProductVariant>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/variants/${variantId}`,
      data
    )
  },

  async deleteVariant(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    variantId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/variants/${variantId}`
    )
  },

  // Addon management
  async createAddon(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    data: CreateAddonRequest
  ): Promise<ProductAddon> {
    return apiService.post<ProductAddon>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/addons`,
      data
    )
  },

  async updateAddon(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    addonId: string,
    data: Partial<CreateAddonRequest>
  ): Promise<ProductAddon> {
    return apiService.put<ProductAddon>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/addons/${addonId}`,
      data
    )
  },

  async deleteAddon(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    addonId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/addons/${addonId}`
    )
  },

  // iMoje configuration
  async updateImojeConfig(
    restaurantId: string,
    data: UpdateImojeConfigRequest
  ): Promise<Restaurant> {
    return apiService.put<Restaurant>(`/restaurants/${restaurantId}/imoje-config`, data)
  },

  async updateSmsConfig(restaurantId: string, data: { smsEnabled?: boolean }): Promise<Restaurant> {
    return apiService.put<Restaurant>(`/restaurants/${restaurantId}/sms-config`, data)
  },

  async updateSmsLimit(restaurantId: string, smsLimit: number | null): Promise<Restaurant> {
    return apiService.put<Restaurant>(`/admin/restaurants/${restaurantId}/sms-limit`, { smsLimit })
  },

  async sendOrderReadySms(orderId: string): Promise<void> {
    return apiService.post<void>(`/orders/${orderId}/send-ready-sms`)
  },

  // Orders management
  async getOrders(
    restaurantId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    orders: Order[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    return apiService.get<{
      orders: Order[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>(`/restaurants/${restaurantId}/orders?page=${page}&limit=${limit}`)
  },

  async getActiveOrders(restaurantId: string): Promise<Order[]> {
    return apiService.get<Order[]>(`/restaurants/${restaurantId}/active-orders`)
  },

  async getOrder(restaurantId: string, orderId: string): Promise<Order> {
    return apiService.get<Order>(`/restaurants/${restaurantId}/orders/${orderId}`)
  },

  async updateOrderStatus(restaurantId: string, orderId: string, status: string): Promise<Order> {
    return apiService.put<Order>(`/restaurants/${restaurantId}/orders/${orderId}/status`, {
      status,
    })
  },

  async markWaiterServiceCompleted(orderId: string): Promise<Order> {
    return apiService.post<Order>(`/orders/${orderId}/waiter-service-completed`)
  },

  async updateOrderItemStatus(
    restaurantId: string,
    orderId: string,
    itemId: string,
    status: string
  ): Promise<OrderItem> {
    return apiService.put<OrderItem>(
      `/restaurants/${restaurantId}/orders/${orderId}/items/${itemId}/status`,
      { status }
    )
  },

  // Section Product Link management (attach/detach/reorder)
  async attachProductToSection(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string,
    sortOrder?: number
  ): Promise<any> {
    return apiService.post(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/attach`,
      { productId, sortOrder }
    )
  },

  async detachProductFromSection(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    productId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/${productId}/detach`
    )
  },

  async reorderSectionProducts(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    items: Array<{ productId: string; sortOrder: number }>
  ): Promise<void> {
    return apiService.put(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/reorder`,
      { items }
    )
  },

  async getSectionProductsWithLinks(
    restaurantId: string,
    menuId: string,
    sectionId: string
  ): Promise<Product[]> {
    return apiService.get<Product[]>(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/products/links`
    )
  },

  // Independent Product management
  async getAllProducts(
    restaurantId: string,
    search?: string,
    includeArchived?: boolean
  ): Promise<Product[]> {
    const query = new URLSearchParams()
    if (search) {
      query.append("search", search)
    }
    if (includeArchived) {
      query.append("includeArchived", "true")
    }
    const paramsString = query.toString()
    const suffix = paramsString ? `?${paramsString}` : ""
    return apiService.get<Product[]>(`/restaurants/${restaurantId}/products${suffix}`)
  },

  async getAllProductsPaginated(
    restaurantId: string,
    page: number = 1,
    limit: number = 20,
    search?: string,
    includeArchived?: boolean
  ): Promise<{
    data: Product[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (search) params.append("search", search)
    if (includeArchived) params.append("includeArchived", "true")
    return apiService.get<{
      data: Product[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>(`/restaurants/${restaurantId}/products?${params.toString()}`)
  },

  async getProduct(restaurantId: string, productId: string): Promise<Product> {
    return apiService.get<Product>(`/restaurants/${restaurantId}/products/${productId}`)
  },

  async uploadProductImage(
    restaurantId: string,
    productId: string,
    imageFile: File
  ): Promise<{
    url: string
    key: string
    image: { original: string; webp: string; avif: string }
  }> {
    const formData = new FormData()
    formData.append("image", imageFile)

    return apiService.post(`/restaurants/${restaurantId}/products/${productId}/image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for image upload and processing
    })
  },

  async uploadRestaurantLogo(
    restaurantId: string,
    imageFile: File
  ): Promise<{
    url: string
    key: string
    image: { original: string; webp: string; avif: string }
  }> {
    const formData = new FormData()
    formData.append("image", imageFile)

    return apiService.post(`/restaurants/${restaurantId}/logo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for image upload and processing
    })
  },

  async uploadRestaurantCoverImage(
    restaurantId: string,
    imageFile: File
  ): Promise<{
    url: string
    key: string
    image: { original: string; webp: string; avif: string }
  }> {
    const formData = new FormData()
    formData.append("image", imageFile)

    return apiService.post(`/restaurants/${restaurantId}/cover-image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for image upload and processing
    })
  },

  async createProductIndependent(
    restaurantId: string,
    data: CreateProductRequest
  ): Promise<Product> {
    return apiService.post<Product>(`/restaurants/${restaurantId}/products`, data)
  },

  async updateProductIndependent(
    restaurantId: string,
    productId: string,
    data: Partial<CreateProductRequest>
  ): Promise<Product> {
    return apiService.put<Product>(`/restaurants/${restaurantId}/products/${productId}`, data)
  },

  async deleteProductIndependent(restaurantId: string, productId: string): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/products/${productId}`)
  },

  async restoreProductIndependent(restaurantId: string, productId: string): Promise<Product> {
    return apiService.post<Product>(
      `/restaurants/${restaurantId}/products/${productId}/restore`,
      {}
    )
  },

  // Product Variants (independent endpoints)
  async createVariantIndependent(
    restaurantId: string,
    productId: string,
    data: CreateVariantRequest
  ): Promise<ProductVariant> {
    return apiService.post<ProductVariant>(
      `/restaurants/${restaurantId}/products/${productId}/variants`,
      data
    )
  },

  async updateVariantIndependent(
    restaurantId: string,
    productId: string,
    variantId: string,
    data: Partial<CreateVariantRequest>
  ): Promise<ProductVariant> {
    return apiService.put<ProductVariant>(
      `/restaurants/${restaurantId}/products/${productId}/variants/${variantId}`,
      data
    )
  },

  async deleteVariantIndependent(
    restaurantId: string,
    productId: string,
    variantId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/products/${productId}/variants/${variantId}`
    )
  },

  // Product Addons (independent endpoints)
  async createAddonIndependent(
    restaurantId: string,
    productId: string,
    data: CreateAddonRequest
  ): Promise<ProductAddon> {
    return apiService.post<ProductAddon>(
      `/restaurants/${restaurantId}/products/${productId}/addons`,
      data
    )
  },

  async updateAddonIndependent(
    restaurantId: string,
    productId: string,
    addonId: string,
    data: Partial<CreateAddonRequest>
  ): Promise<ProductAddon> {
    return apiService.put<ProductAddon>(
      `/restaurants/${restaurantId}/products/${productId}/addons/${addonId}`,
      data
    )
  },

  async deleteAddonIndependent(
    restaurantId: string,
    productId: string,
    addonId: string
  ): Promise<void> {
    return apiService.delete(`/restaurants/${restaurantId}/products/${productId}/addons/${addonId}`)
  },

  // Product Translations
  async createProductTranslation(
    restaurantId: string,
    productId: string,
    data: { language: string; name: string; description?: string }
  ): Promise<any> {
    return apiService.post(`/restaurants/${restaurantId}/products/${productId}/translations`, data)
  },

  async updateProductTranslation(
    restaurantId: string,
    productId: string,
    translationId: string,
    data: { language?: string; name?: string; description?: string }
  ): Promise<any> {
    return apiService.put(
      `/restaurants/${restaurantId}/products/${productId}/translations/${translationId}`,
      data
    )
  },

  // Section Translations
  async createSectionTranslation(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    data: { language: string; name: string; description?: string }
  ): Promise<any> {
    return apiService.post(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/translations`,
      data
    )
  },

  async updateSectionTranslation(
    restaurantId: string,
    menuId: string,
    sectionId: string,
    translationId: string,
    data: { name?: string; description?: string }
  ): Promise<any> {
    return apiService.put(
      `/restaurants/${restaurantId}/menu/${menuId}/sections/${sectionId}/translations/${translationId}`,
      data
    )
  },

  async deleteProductTranslation(
    restaurantId: string,
    productId: string,
    translationId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/products/${productId}/translations/${translationId}`
    )
  },

  // Variant Translations
  async createVariantTranslation(
    restaurantId: string,
    productId: string,
    variantId: string,
    data: { language: string; name: string; description?: string }
  ): Promise<any> {
    return apiService.post(
      `/restaurants/${restaurantId}/products/${productId}/variants/${variantId}/translations`,
      data
    )
  },

  async updateVariantTranslation(
    restaurantId: string,
    productId: string,
    variantId: string,
    translationId: string,
    data: { language?: string; name?: string; description?: string }
  ): Promise<any> {
    return apiService.put(
      `/restaurants/${restaurantId}/products/${productId}/variants/${variantId}/translations/${translationId}`,
      data
    )
  },

  async deleteVariantTranslation(
    restaurantId: string,
    productId: string,
    variantId: string,
    translationId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/products/${productId}/variants/${variantId}/translations/${translationId}`
    )
  },

  // Product Addon Translation functions
  async createAddonTranslation(
    restaurantId: string,
    productId: string,
    addonId: string,
    data: { language: string; name: string; description?: string }
  ): Promise<any> {
    return apiService.post(
      `/restaurants/${restaurantId}/products/${productId}/addons/${addonId}/translations`,
      data
    )
  },

  async updateAddonTranslation(
    restaurantId: string,
    productId: string,
    addonId: string,
    translationId: string,
    data: { language: string; name: string; description?: string }
  ): Promise<any> {
    return apiService.put(
      `/restaurants/${restaurantId}/products/${productId}/addons/${addonId}/translations/${translationId}`,
      data
    )
  },

  async deleteAddonTranslation(
    restaurantId: string,
    productId: string,
    addonId: string,
    translationId: string
  ): Promise<void> {
    return apiService.delete(
      `/restaurants/${restaurantId}/products/${productId}/addons/${addonId}/translations/${translationId}`
    )
  },

  // Cart functions
  async getRestaurantCarts(restaurantId: string): Promise<any[]> {
    return apiService.get(`/restaurants/${restaurantId}/carts`)
  },

  // Reorder functions
  async reorderVariants(
    restaurantId: string,
    productId: string,
    reorderData: { variantId: string; sortOrder: number }[]
  ): Promise<void> {
    return apiService.put(
      `/restaurants/${restaurantId}/products/${productId}/variants/reorder`,
      reorderData
    )
  },

  async reorderAddons(
    restaurantId: string,
    productId: string,
    reorderData: { addonId: string; sortOrder: number }[]
  ): Promise<void> {
    return apiService.put(
      `/restaurants/${restaurantId}/products/${productId}/addons/reorder`,
      reorderData
    )
  },

  // Superadmin methods
  async getAllOrders(): Promise<Order[]> {
    return apiService.get<Order[]>("/admin/orders")
  },

  async getAllOrdersPaginated(
    page: number,
    limit: number,
    restaurantId?: string,
    status?: string,
    paymentStatus?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<{
    data: Order[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (restaurantId) params.append("restaurantId", restaurantId)
    if (status) params.append("status", status)
    if (paymentStatus) params.append("paymentStatus", paymentStatus)
    if (dateFrom) params.append("dateFrom", dateFrom)
    if (dateTo) params.append("dateTo", dateTo)

    return apiService.get<{
      data: Order[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>(`/admin/orders/paginated?${params.toString()}`)
  },

  async getUserOrders(userId: string): Promise<Order[]> {
    return apiService.get<Order[]>(`/admin/users/${userId}/orders`)
  },

  // Menu import
  async importMenuFromJson(
    restaurantId: string,
    jsonFile: File
  ): Promise<{
    success: boolean
    summary: {
      sectionsCreated: number
      sectionsSkipped?: number
      productsCreated: number
      variantsCreated: number
      addonsCreated: number
      errors: number
    }
    errors?: Array<{
      type: string
      message: string
      details?: any
    }>
    productsNeedingImages?: Array<{
      name: string
      productId: string
      oldImageUrl: string
    }>
  }> {
    const formData = new FormData()
    formData.append("file", jsonFile)

    return apiService.post(`/restaurants/${restaurantId}/menu/import`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 120000, // 120 seconds for menu import
    })
  },

  async parseMenuDocument(
    restaurantId: string,
    file: File
  ): Promise<{
    menu: {
      name: string
      menu_sections: Array<{
        name: string
        products: Array<{
          name: string
          description?: string
          price_cents: number
          variants?: Array<{ name: string; default: string; price_cents: number }>
          addons?: Array<{ name: string; price_cents: number }>
        }>
      }>
    }
  }> {
    const formData = new FormData()
    formData.append("file", file)
    return apiService.post(
      `/restaurants/${restaurantId}/menu/parse-document`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      }
    )
  },

  async importMenuFromJsonBody(
    restaurantId: string,
    data: {
      menu: {
        name: string
        menu_sections: Array<{
          name: string
          products: Array<{
            name: string
            description?: string
            price_cents: number
            variants?: Array<{ name: string; default: string; price_cents: number }>
            addons?: Array<{ name: string; price_cents: number }>
          }>
        }>
      }
    }
  ): Promise<{
    success: boolean
    summary: {
      sectionsCreated: number
      productsCreated: number
      variantsCreated: number
      addonsCreated: number
      errors: number
    }
  }> {
    return apiService.post(
      `/restaurants/${restaurantId}/menu/import-json`,
      data,
      { timeout: 120000 }
    )
  },

  // Menu translations
  async getTranslationStatus(
    restaurantId: string,
    menuId: string
  ): Promise<{
    inProgress: boolean
    completed: boolean
    status: {
      menuId: string
      restaurantId: string
      targetLanguages: string[]
      startedAt: string
      completedAt?: string
      menuName?: string
      error?: string
      jobId?: string
    } | null
    result?: any
  }> {
    return apiService.get<{
      inProgress: boolean
      completed: boolean
      status: {
        menuId: string
        restaurantId: string
        targetLanguages: string[]
        startedAt: string
        completedAt?: string
        menuName?: string
        error?: string
        jobId?: string
      } | null
      result?: any
    }>(`/restaurants/${restaurantId}/menu/${menuId}/translations/status`)
  },

  async generateMenuTranslations(
    restaurantId: string,
    menuId: string,
    targetLanguages: string[]
  ): Promise<any> {
    return apiService.post<any>(
      `/restaurants/${restaurantId}/menu/${menuId}/translations/generate`,
      { targetLanguages }
    )
  },

  async saveMenuTranslations(
    restaurantId: string,
    menuId: string,
    translations: any,
    jobId?: string
  ): Promise<{ saved: number; errors: number }> {
    return apiService.post<{ saved: number; errors: number }>(
      `/restaurants/${restaurantId}/menu/${menuId}/translations/save`,
      { translations, jobId }
    )
  },

  async getTranslationHistory(
    restaurantId: string,
    menuId?: string
  ): Promise<
    Array<{
      id: string
      menuId: string
      menuName: string
      targetLanguages: string[]
      status: "in_progress" | "completed" | "failed" | "pending_review" | "accepted"
      startedAt: string
      completedAt: string | null
      acceptedAt: string | null
      error: string | null
      createdAt: string
    }>
  > {
    const params = menuId ? `?menuId=${menuId}` : ""
    return apiService.get(
      `/restaurants/${restaurantId}/menu/translations/history${params}`
    )
  },

  async acceptTranslationJob(
    restaurantId: string,
    jobId: string
  ): Promise<void> {
    return apiService.post(
      `/restaurants/${restaurantId}/menu/translations/${jobId}/accept`,
      {}
    )
  },
}
